















































































import {
computed,
defineComponent,
onMounted,
ref,
useContext,
useRouter,
} from '@nuxtjs/composition-api';
import {
SfHeading,
} from '@storefront-ui/vue';
import BKButton from '~/components/BK/BKButton.vue';
import { useCart, useCountrySearch } from '~/composables';
import { mergeItem } from '~/helpers/asyncLocalStorage';
import {
CheckoutAddressForm,
addressFromApiToForm,
findUserAddressIdenticalToSavedCartAddress,
} from '~/helpers/checkout/address';
import { isPreviousStepValid } from '~/helpers/checkout/steps';
import { isBikeProduct } from '~/helpers/bikeHelper';
import type {
AvailableShippingMethod,
ConfigurableCartItem,
Country,
Customer,
CustomerAddress,
} from '~/modules/GraphQL/types';
import useShipping from '~/modules/checkout/composables/useShipping';
import { TransformedCustomerAddress } from '~/modules/customer/composables/types';
import useUser from '~/modules/customer/composables/useUser';
import useUserAddress from '~/modules/customer/composables/useUserAddress';
import addressGetter from '~/modules/customer/getters/addressGetter';
import userShippingGetters from '~/modules/customer/getters/userShippingGetters';
import AddressForm from '~/modules/customer/pages/MyAccount/AddressesDetails/AddressForm.vue';

export default defineComponent({
  name: 'ShippingStep',
  components: {
    SfHeading,
    AddressForm,
    BKButton,
    UserShippingAddresses: () => import('~/modules/checkout/components/UserShippingAddresses.vue'),
    VsfShippingProvider: () => import('~/modules/checkout/components/VsfShippingProvider.vue'),
  },
  setup() {
    const router = useRouter();
    const { app } = useContext();
    const userShipping = ref<Customer | null>(null);
    const {
      load: loadShipping,
      save: saveShipping,
      loading: isShippingLoading,
    } = useShipping();
    const {
      load: loadUserShipping,
      setDefaultAddress,
    } = useUserAddress();

    const {
      load: loadCountries,
      search: searchCountry,
    } = useCountrySearch();

    const {
      cart,
    } = useCart();

    const countries = ref<Country[]>([]);
    const country = ref<Country | null>(null);
    const {
      isAuthenticated,
      user,
    } = useUser();
    const initialShippingDetails = {
      apartment: '',
      neighborhood: '',
      extra: '',
      phone: '',
      email: '',
    };
    const shippingDetails = ref<TransformedCustomerAddress>(initialShippingDetails);
    const shippingMethods = ref<AvailableShippingMethod[]>([]);
    const currentAddressId = ref<number | null>(null);

    const isSetAsDefaultRequested = ref(false);
    const isFormSubmitted = ref(false);
    const isAddNewAddressFormVisible = ref(true);

    const isShippingDetailsStepCompleted = ref(false);
    const addresses = computed(() => userShippingGetters.getAddresses(userShipping.value));

    const canMoveForward = computed(() => !isShippingLoading.value && shippingDetails.value && Object.keys(
      shippingDetails.value,
    ).length > 0);

    const hasSavedShippingAddress = computed(() => {
      if (!isAuthenticated.value || !userShipping.value) {
        return false;
      }
      return addresses.value.length > 0;
    });

    const countriesList = computed(() => addressGetter.countriesList(countries.value));
    const cartHasBike = computed(() => !!cart.value?.items?.some((item: ConfigurableCartItem) => isBikeProduct(item.product)));

    const handleAddressSubmit = async (form?: any) => {
      const { phone } = { ...shippingDetails.value };
      delete shippingDetails.value.phone;

      let cleanedForm: any = {};

      const addressId = currentAddressId.value;
      const shippingDetailsData = {
        ...shippingDetails.value,
        telephone: phone,
        customerAddressId: addressId,
        save_in_address_book: false,
      };

      // TODO clean form

      if (form) {
        cleanedForm = { ...form };
        cleanedForm.form.region = form.form?.region?.region_code;
        delete cleanedForm.form.default_billing;
        delete cleanedForm.form.default_shipping;
      }

      await mergeItem('checkout', { shipping: cleanedForm?.form || shippingDetailsData });

      const shippingInfo = await saveShipping({ shippingDetails: cleanedForm?.form || shippingDetailsData });

      shippingMethods.value = computed(() => (!cartHasBike.value
        ? [shippingInfo?.available_shipping_methods.find((shippingMethod: AvailableShippingMethod) => shippingMethod.carrier_title === 'Abholung bei uns im Store')] ?? []
        : shippingInfo?.available_shipping_methods ?? [])).value;

      if (addressId !== null && isSetAsDefaultRequested.value) {
        const [chosenAddress] = userShippingGetters.getAddresses(
          userShipping.value,
          { id: addressId },
        );
        chosenAddress.default_shipping = isSetAsDefaultRequested.value;
        if (chosenAddress) {
          await setDefaultAddress({ address: chosenAddress });
          userShipping.value = await loadUserShipping(true);
        }
      }

      isShippingDetailsStepCompleted.value = true;
    };

    const handleAddNewAddressBtnClick = () => {
      currentAddressId.value = null;
      shippingDetails.value = initialShippingDetails;
      isAddNewAddressFormVisible.value = true;
      isShippingDetailsStepCompleted.value = false;
    };

    const handleSetCurrentAddress = async (customerAddress: CustomerAddress) => {
      const id = customerAddress?.id;
      currentAddressId.value = id;
      if (id) {
        isAddNewAddressFormVisible.value = false;
      }
      shippingDetails.value = addressFromApiToForm(customerAddress);
      country.value = customerAddress.country_code ? await searchCountry({ id: customerAddress.country_code }) : null;
      isShippingDetailsStepCompleted.value = false;
    };

    const changeShippingDetails = (field: keyof CheckoutAddressForm, value: string) => {
      (shippingDetails.value[field] as string) = value;
      isShippingDetailsStepCompleted.value = false;
      currentAddressId.value = null;
    };

    const changeCountry = async (id: string) => {
      changeShippingDetails('country_code', id);
      const newCountry = await searchCountry({ id });
      shippingDetails.value.region = {};
      country.value = newCountry;
    };

    onMounted(async () => {
      if (isAuthenticated.value) {
        await mergeItem('checkout', { 'user-account': user });
      }
      const validStep = await isPreviousStepValid('user-account');
      if (!validStep) {
        await router.push(app.localeRoute({ name: 'user-account' }));
      }
      const [loadedShippingInfoBoundToCart, loadedUserShipping, loadedCountries] = await Promise.all([
        loadShipping(),
        loadUserShipping(),
        loadCountries(),
      ]);
      const [defaultAddress = null] = userShippingGetters.getAddresses(loadedUserShipping, { default_shipping: true });
      const wasShippingAddressAlreadySetOnCart = Boolean(loadedShippingInfoBoundToCart);

      if (wasShippingAddressAlreadySetOnCart) {
        const userAddressIdenticalToSavedCartAddress = findUserAddressIdenticalToSavedCartAddress(
          loadedUserShipping?.addresses,
          loadedShippingInfoBoundToCart,
        );
        handleSetCurrentAddress({ ...loadedShippingInfoBoundToCart, id: userAddressIdenticalToSavedCartAddress?.id });
      } else if (defaultAddress) {
        handleSetCurrentAddress(defaultAddress);
      }
      if (shippingDetails.value?.country_code) {
        country.value = await searchCountry({ id: shippingDetails.value.country_code });
      }
      userShipping.value = loadedUserShipping;
      countries.value = loadedCountries;
    });

    return {
      isAddNewAddressFormVisible,
      canMoveForward,
      changeCountry,
      changeShippingDetails,
      countries,
      countriesList,
      country,
      currentAddressId,
      handleAddNewAddressBtnClick,
      handleAddressSubmit,
      handleSetCurrentAddress,
      hasSavedShippingAddress,
      isAuthenticated,
      isFormSubmitted,
      isShippingDetailsStepCompleted,
      isShippingLoading,
      searchCountry,
      isSetAsDefaultRequested,
      shippingDetails,
      shippingMethods,
      addresses,
      cartHasBike,
    };
  },
});
